<template>
  <transition name="slide-up">
    <div v-if="isVisible" class="global-generation-progress" :class="{ minimized: isMinimized }">
      <!-- Header -->
      <div class="generation-header">
        <div class="header-left">
          <div class="generation-icon-wrapper">
            <q-icon name="videocam" size="14px" class="generation-icon" />
          </div>
          <div class="header-info">
            <span class="generation-title">
              {{ getHeaderTitle() }}
            </span>
            <span v-if="generations.length > 0" class="generation-subtitle">
              {{ getHeaderSubtitle() }}
            </span>
          </div>
        </div>

        <div class="header-actions">
          <q-btn flat dense round size="sm" :icon="isMinimized ? 'keyboard_arrow_up' : 'keyboard_arrow_down'" @click="toggleMinimize" class="header-btn" />
          <q-btn flat dense round size="sm" icon="close" @click="handleClose" class="header-btn" />
        </div>
      </div>

      <!-- Content -->
      <div v-if="!isMinimized" class="generation-content">
        <!-- Generation items list -->
        <div v-if="generations.length > 0" class="generation-list">
          <div v-for="generation in generations" :key="generation.id" class="generation-item">
            <!-- Left: Image thumbnail -->
            <div class="generation-thumbnail">
              <img v-if="generation.imageUrl" :src="generation.imageUrl" :alt="generation.fileName || 'AI Generated Video'" class="thumbnail-image" @error="handleImageError" />
              <div v-else class="thumbnail-fallback">
                <q-icon name="image" size="20px" color="grey-6" />
              </div>
            </div>

            <!-- Middle: Generation info -->
            <div class="generation-info">
              <div class="generation-main-info">
                <q-icon name="videocam" size="16px" color="grey-7" class="generation-type-icon" />
                <span class="generation-name">{{ truncateFileName(generation.fileName || 'AI Video Generation', 20) }}</span>
              </div>
              <div class="generation-details">
                <span class="camera-effect">{{ generation.cameraEffect }}</span>
                <span v-if="formattedTimeRemaining(generation)" class="time-remaining">
                  {{ formattedTimeRemaining(generation) }}
                </span>
              </div>
            </div>

            <!-- Right: Action area -->
            <div class="generation-actions">
              <!-- Remove button for completed generations -->
              <q-btn
                v-if="!shouldShowProgress(generation) && generation.progress >= 100 && generation.status !== 'error'"
                flat
                dense
                round
                size="sm"
                icon="close"
                color="grey-6"
                class="action-btn remove-btn"
                @click="handleRemove(generation)"
              >
                <q-tooltip>Remove from list</q-tooltip>
              </q-btn>

              <!-- Progress indicator for active generations -->
              <div v-if="shouldShowProgress(generation)" class="progress-indicator">
                <q-circular-progress
                  :value="getAnimatedProgress(generation)"
                  size="24px"
                  :thickness="0.4"
                  :color="getProgressColor(generation)"
                  track-color="grey-3"
                  class="generation-progress"
                  :class="{ 'progress-active': shouldShowProgress(generation) }"
                />
                <span class="progress-text">{{ Math.round(getAnimatedProgress(generation)) }}%</span>
              </div>

              <!-- Warning icon for completed with warnings -->
              <div v-else-if="!shouldShowProgress(generation) && generation.progress >= 100 && generation.status === 'warning'" class="warning-indicator">
                <q-icon name="warning" size="24px" color="orange">
                  <q-tooltip class="warning-tooltip" anchor="top middle" self="bottom middle" :offset="[0, 8]" :z-index="99999">
                    <div class="tooltip-content">
                      <div class="tooltip-message">{{ generation.message || 'Generation completed with issues' }}</div>
                    </div>
                  </q-tooltip>
                </q-icon>
              </div>

              <!-- Success checkmark -->
              <div v-else-if="!shouldShowProgress(generation) && generation.progress >= 100 && generation.status !== 'error' && generation.status !== 'warning'" class="success-indicator">
                <q-icon name="check_circle" size="24px" color="positive">
                  <q-tooltip class="success-tooltip" anchor="top middle" self="bottom middle" :offset="[0, 8]" :z-index="99999">
                    <div class="tooltip-content">
                      <div v-if="generation.message" class="tooltip-message">{{ generation.message }}</div>
                      <div v-else class="tooltip-message">Video generated successfully</div>
                    </div>
                  </q-tooltip>
                </q-icon>
              </div>

              <!-- Error icon for failed generations -->
              <div v-else-if="generation.status === 'error'" class="error-indicator">
                <q-icon name="error" size="24px" color="negative">
                  <q-tooltip class="error-tooltip" anchor="top middle" self="bottom middle" :offset="[0, 8]" :z-index="99999">
                    <div class="tooltip-content">
                      <div class="tooltip-message">{{ generation.message || 'An error occurred during generation' }}</div>
                    </div>
                  </q-tooltip>
                </q-icon>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { computed, ref, watch } from '@vue/composition-api';
import { useQuery } from '@tanstack/vue-query';

export default {
  name: 'GlobalVideoGenerationProgress',

  setup(props, { root }) {
    // Reactive data
    const isMinimized = ref(false);

    // Computed properties from Vuex store
    const isVisible = computed(() => {
      const generations = root.$store.getters['videoGenerationProgress/generations'];
      return root.$store.getters['videoGenerationProgress/isVisible'] && generations.length > 0;
    });

    const generations = computed(() => root.$store.getters['videoGenerationProgress/generations']);

    const activeGenerations = computed(() => generations.value.filter(gen => gen.progress < 100));

    const completedGenerations = computed(() => generations.value.filter(gen => gen.progress >= 100));

    const totalActiveGenerations = computed(() => generations.value.filter(gen => gen.progress < 100 && gen.status !== 'error').length);

    const hasActiveGenerations = computed(() => generations.value.some(gen => gen.progress < 100 && gen.status !== 'error'));

    const generationsNeedingCheck = computed(() => generations.value.filter(gen => gen.predictionId && gen.status !== 'error' && gen.progress < 100));

    // TanStack Query for API status checking only (progress is now synced from VideoGenerator)
    const generationStatusQuery = useQuery({
      queryKey: ['video-generation-status', computed(() => generationsNeedingCheck.value.map(g => g.predictionId))],
      queryFn: async () => {
        return await root.$store.dispatch('videoGenerationProgress/getGenerationStatus');
      },
      enabled: computed(() => generationsNeedingCheck.value.length > 0),
      refetchInterval: 5000, // Check API status every 5 seconds (reduced frequency since progress is handled by VideoGenerator)
      refetchIntervalInBackground: true,
      staleTime: 0, // Always consider data stale to ensure fresh polling
      refetchOnWindowFocus: false, // Don't refetch when window gains focus
      retryDelay: 1000, // Retry faster on errors
      retry: 3, // Retry up to 3 times
      onSuccess: data => {
        // Update the Vuex store with the new progress data
        if (data && data.length > 0) {
          root.$store.dispatch('videoGenerationProgress/updateGenerationsFromStatus', data);
        }
      },
      onError: error => {
        console.error('Error fetching generation progress:', error);
      }
    });

    // TanStack Query for global content processing status polling
    // This tracks all video generations across the entire app, even those started elsewhere
    const globalProcessingQuery = useQuery({
      queryKey: ['global-processing-status'],
      queryFn: async () => {
        return await root.$store.dispatch('videoGenerationProgress/getGlobalProcessingStatus');
      },
      enabled: computed(() => {
        // Enable global polling when:
        // 1. We have active generations in the store, OR
        // 2. The dialog is visible (user is actively monitoring)
        return hasActiveGenerations.value || isVisible.value;
      }),
      refetchInterval: 10000, // Poll global status every 10 seconds (less frequent than individual status checks)
      refetchIntervalInBackground: true,
      staleTime: 5000, // Consider data stale after 5 seconds
      refetchOnWindowFocus: false, // Don't refetch when window gains focus
      retryDelay: 2000, // Slower retry on errors for global endpoint
      retry: 2, // Fewer retries for global endpoint
      onSuccess: data => {
        // Update the Vuex store with global status updates
        if (data && data.length > 0) {
          root.$store.dispatch('videoGenerationProgress/updateGenerationsFromStatus', data);
        }
      },
      onError: error => {
        console.error('Error fetching global processing status:', error);
      }
    });

    // No need for periodic cleanup - we use clean slate approach on page reload

    // Simplified check if upload progress is visible (for context only)
    const isUploadProgressVisible = computed(() => {
      const uploads = root.$store.getters['uploadProgress/uploads'] || [];
      return root.$store.getters['uploadProgress/isVisible'] && uploads.length > 0;
    });

    // Store actions
    const hideDialog = () => root.$store.dispatch('videoGenerationProgress/hideDialog');
    const clearCompleted = () => root.$store.dispatch('videoGenerationProgress/clearCompleted');
    const clearAll = () => root.$store.dispatch('videoGenerationProgress/clearAll');

    // Cleanup function (simplified)
    const cleanup = () => {
      // No intervals to clean up in simplified version
    };

    // Setup cleanup on component unmount
    root.$once('hook:beforeDestroy', cleanup);

    // Methods
    const toggleMinimize = () => {
      isMinimized.value = !isMinimized.value;
    };

    const handleClose = () => {
      hideDialog();
    };

    // Get progress value directly from store (synchronized with VideoGenerator)
    const getAnimatedProgress = generation => {
      // Use the actual progress from the store, which is now synchronized with VideoGenerator
      return generation.progress || 0;
    };

    const getProgressColor = generation => {
      if (generation.status === 'error') return 'negative';
      if (generation.progress >= 100) return 'positive';
      return 'primary';
    };

    const shouldShowProgress = generation => {
      // Show progress indicator based on actual progress from store (synchronized with VideoGenerator)
      return generation.progress < 100 && generation.status !== 'error';
    };

    const handleImageError = event => {
      const target = event.target;
      target.style.display = 'none';
      target.parentElement.classList.add('image-error');
    };

    const handleRemove = generation => {
      root.$store.dispatch('videoGenerationProgress/removeGeneration', generation.id);

      root.$q.notify({
        type: 'info',
        message: 'Video generation removed from list',
        position: 'top',
        timeout: 2000
      });
    };

    const truncateFileName = (fileName, maxLength) => {
      if (fileName.length > maxLength) {
        return fileName.slice(0, maxLength) + '...';
      }
      return fileName;
    };

    const getHeaderTitle = () => {
      if (generations.value.length === 0) {
        return 'Video Generation';
      }

      const hasActiveGenerationsValue = generations.value.some(gen => gen.progress < 100 && gen.status !== 'error');
      const hasErrors = generations.value.some(gen => gen.status === 'error');

      if (hasActiveGenerationsValue) {
        return 'Generating videos...';
      } else if (hasErrors && generations.value.every(gen => gen.status === 'error' || gen.progress >= 100)) {
        return 'Generation completed with errors';
      } else {
        return 'Generation completed';
      }
    };

    const getHeaderSubtitle = () => {
      if (generations.value.length === 0) {
        return '';
      }

      const videoText = generations.value.length === 1 ? 'video' : 'videos';
      return `${generations.value.length} image-to-${videoText} generation`;
    };

    // Calculate time remaining using exact same logic as VideoGenerator
    const formattedTimeRemaining = generation => {
      // Use progressStartTime if available (synced from VideoGenerator), otherwise fall back to startTime
      const startTime = generation.progressStartTime || new Date(generation.startTime).getTime();

      if (!startTime || generation.progress >= 100 || generation.status === 'error') {
        return null;
      }

      const elapsed = Date.now() - startTime;
      const progressDuration = 240000; // 4 minutes in milliseconds (same as VideoGenerator)
      const progressDecimal = generation.progress / 100;

      // Avoid division by zero and handle very small progress values (exact same logic as VideoGenerator)
      if (progressDecimal <= 0.01) {
        // For very early progress, show the full duration minus elapsed time
        const remaining = Math.max(0, progressDuration - elapsed);
        return formatTimeRemaining(remaining);
      }

      // For progress near completion (95%+), show a small remaining time (exact same logic as VideoGenerator)
      if (progressDecimal >= 0.95) {
        const remaining = Math.min(30000, Math.max(5000, progressDuration - elapsed)); // 5-30 seconds
        return formatTimeRemaining(remaining);
      }

      // Calculate estimated total time based on current progress (exact same logic as VideoGenerator)
      const estimatedTotalTime = elapsed / progressDecimal;
      const remainingTime = estimatedTotalTime - elapsed;

      // Handle edge cases and apply reasonable bounds (exact same logic as VideoGenerator)
      if (remainingTime <= 0) {
        return formatTimeRemaining(5000); // Show at least 5 seconds remaining
      }

      // Cap the maximum remaining time to avoid unrealistic estimates (exact same logic as VideoGenerator)
      const maxRemainingTime = progressDuration - elapsed + 30000; // Add 30s buffer
      const boundedRemainingTime = Math.min(remainingTime, maxRemainingTime);

      return formatTimeRemaining(boundedRemainingTime);
    };

    const formatTimeRemaining = remaining => {
      if (remaining === null || remaining <= 0) {
        return null;
      }

      const totalSeconds = Math.ceil(remaining / 1000);

      // Show "Almost done..." for very short remaining times
      if (totalSeconds <= 10) {
        return 'Almost done...';
      }

      if (totalSeconds < 60) {
        return `${totalSeconds}s remaining`;
      }

      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;

      if (minutes < 60) {
        return seconds > 0 ? `${minutes}m ${seconds}s remaining` : `${minutes}m remaining`;
      }

      // For times over an hour (shouldn't happen with 4min duration, but just in case)
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `${hours}h ${remainingMinutes}m remaining`;
    };

    // Watch for route changes to reopen dialog if there are active generations
    watch(
      () => root.$route.path,
      (newPath, oldPath) => {
        if (newPath !== oldPath && hasActiveGenerations.value && !isVisible.value) {
          root.$store.dispatch('videoGenerationProgress/showDialog');
        }
      },
      { immediate: false }
    );

    return {
      // Reactive data
      isMinimized,
      cleanup,

      // Computed
      isVisible,
      isUploadProgressVisible,
      generations,
      activeGenerations,
      completedGenerations,
      totalActiveGenerations,
      hasActiveGenerations,
      generationsNeedingCheck,

      // Query
      generationStatusQuery,

      // Methods
      toggleMinimize,
      handleClose,
      hideDialog,
      clearCompleted,
      clearAll,
      getAnimatedProgress,
      getProgressColor,
      shouldShowProgress,
      handleImageError,
      handleRemove,
      truncateFileName,
      getHeaderTitle,
      getHeaderSubtitle,
      formattedTimeRemaining,
      formatTimeRemaining,
      globalProcessingQuery
    };
  },

  // No watchers needed - progress is now synchronized directly from VideoGenerator

  beforeDestroy() {
    // Cleanup any remaining intervals
    this.cleanup();
  }
};
</script>

<style lang="scss" scoped>
.global-generation-progress {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: var(--secondary-bg-color);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.08);
  width: 360px;
  z-index: 10001; // Lower z-index - upload progress will stack above
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &.minimized {
    .generation-content {
      display: none;
    }
  }
}

.generation-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: var(--secondary-bg-color);
  border-radius: 12px 12px 0 0;
  min-height: 52px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.generation-icon-wrapper {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--video-chip-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.generation-icon {
  color: var(--video-chip-text);
  flex-shrink: 0;
}

.header-info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  min-width: 0;
}

.generation-title {
  @include mona-sans-font($size: 16px, $bolded: true, $color: var(--main-text-color));
  line-height: 1.2;
  margin: 0;
  text-align: left;
}

.generation-subtitle {
  @include inter-font($size: 13px, $color: var(--secondary-text-color));
  line-height: 1.2;
  margin-top: 2px;
  text-align: left;
}

.header-actions {
  display: flex;
  gap: 0;
  align-items: center;
}

.header-btn {
  color: var(--secondary-text-color);
  width: 24px;
  height: 24px;
  border-radius: 4px;

  &:hover {
    color: var(--main-text-color);
    background: rgba(0, 0, 0, 0.05);
  }

  .q-icon {
    font-size: 16px;
  }
}

.generation-content {
  max-height: 280px;
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.generation-list {
  padding: 0;
}

.generation-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  transition: background-color 0.2s ease;

  &:hover {
    background: var(--light-grey-hr);
  }

  &:last-child {
    border-bottom: none;
  }
}

.generation-thumbnail {
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 6px;
  overflow: hidden;
  background: var(--light-grey-hr);
  flex-shrink: 0;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: var(--light-grey-hr);

  &.image-error {
    background: var(--light-grey-hr);
  }
}

.generation-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.generation-main-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.generation-type-icon {
  flex-shrink: 0;
}

.generation-name {
  @include mona-sans-font($size: 13px, $weight: 500, $color: var(--main-text-color));
  line-height: 1.3;
  word-break: break-word;
}

.generation-details {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  flex-wrap: wrap;
}

.camera-effect {
  @include inter-font($size: 12px, $color: var(--primary), $weight: 500);
  line-height: 1.2;
}

.time-remaining {
  @include inter-font($size: 11px, $color: var(--secondary-text-color), $weight: 400);
  line-height: 1.2;
}

.generation-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  min-width: 60px;
  justify-content: flex-end;
}

.action-btn {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &.remove-btn {
    &:hover {
      background: rgba(0, 0, 0, 0.05);
    }
  }
}

.progress-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  gap: 6px;
  min-width: 60px;
}

.progress-text {
  @include inter-font($size: 11px, $weight: 500, $color: var(--main-text-color));
  line-height: 1;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;

  .progress-indicator:has(.progress-active) & {
    animation: textPulse 1.5s ease-in-out infinite;
    color: var(--primary);
    font-weight: 700;
  }

  .progress-active + & {
    animation: textPulse 1.5s ease-in-out infinite;
    color: var(--primary);
    font-weight: 700;
  }
}

.generation-progress {
  transition: all 0.3s ease;

  &.progress-active {
    animation: progressPulse 2s ease-in-out infinite;
  }
}

.success-indicator,
.warning-indicator,
.error-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

@keyframes progressPulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
}

@keyframes textPulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
}

// Removed slideUpFromUpload animation - now using dynamic positioning with smooth transitions

@media (max-width: 480px) {
  .global-generation-progress {
    right: 16px;
    bottom: 16px;
    width: calc(100vw - 32px);
    max-width: 380px;
  }

  .generation-item {
    padding: 10px 16px;
    gap: 10px;
  }

  .generation-thumbnail {
    width: 36px;
    height: 36px;
  }

  .generation-name {
    font-size: 12px;
  }

  .header-left {
    gap: 8px;
  }

  .progress-indicator {
    min-width: 55px;
    gap: 4px;
  }

  .progress-text {
    font-size: 10px;
  }

  .generation-actions {
    min-width: 55px;
  }
}

:deep(.error-tooltip .q-tooltip),
:deep(.success-tooltip .q-tooltip),
:deep(.warning-tooltip .q-tooltip),
.error-tooltip .q-tooltip,
.success-tooltip .q-tooltip,
.warning-tooltip .q-tooltip {
  background: var(--secondary-bg-color) !important;
  padding: 0 !important;
  position: fixed !important;
  white-space: nowrap !important;
}

.tooltip-content {
  padding: 8px 12px;
}

.tooltip-message {
  @include inter-font($size: 11px, $color: #e5e5e5);
  line-height: 1.3;
  word-break: break-word;
}
</style>

<style lang="scss">
.q-tooltip {
  z-index: 99999 !important;
}

.global-generation-progress {
  .q-tooltip {
    z-index: 99999 !important;
  }
}
</style>
